import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import logCustomRoutePOIExploreSelected from '@alltrails/amplitude/events/logCustomRoutePOIExploreSelected';
import { useClientNavigationPushState } from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useValueRef from '@alltrails/core/hooks/useValueRef';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Add from '@alltrails/denali/icons/Add';
import Edit from '@alltrails/denali/icons/Edit';
import Gallery from '@alltrails/denali/icons/Gallery';
import { updateLocationResult } from '@alltrails/explore-service/redux/exploreServiceSlice';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { setPendingFlyTo, updateActivePOI, updateSelectedPOI, updateShowPOIPopupMenu } from '../../../redux/reducer';
import useRouteToPoint from '../../CustomRoute/DrawControls/useRouteToPoint';
import ActivePOIPopupOptions from './ActivePOIPopupOptions';

type ActivePOIRoutingOptionsProps = {
  canAddToRoute?: boolean;
  isTapAnywhereEnabled?: boolean;
};

const ActivePOIRoutingOptions = ({ canAddToRoute = false, isTapAnywhereEnabled = false }: ActivePOIRoutingOptionsProps) => {
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const languageRegionCode = useLanguageRegionCode();
  const pushState = useClientNavigationPushState();
  const { activePOI, isSmartRouting, showPOIPopupMenu } = useAppSelector(state => ({
    activePOI: state.map.activePOI,
    isSmartRouting: state.customRoute.isSmartRouting,
    showPOIPopupMenu: state.map.showPOIPopupMenu
  }));
  const isSmartRoutingRef = useValueRef(isSmartRouting);
  const { routeToPoint } = useRouteToPoint(isSmartRoutingRef);

  const returnTo = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    return window.location.pathname + window.location.search;
  }, []);

  const buildCustomRouteUrl = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    if (activePOI?._geoloc) {
      params.set('lat', activePOI?._geoloc?.lat.toString());
      params.set('lng', activePOI?._geoloc?.lng.toString());
    }
    if (returnTo) {
      params.set('returnTo', returnTo);
    }
    return wrapUrlSafe(`/explore/custom-routes/new?${params.toString()}`, languageRegionCode);
  }, [searchParams, returnTo, languageRegionCode, activePOI?._geoloc]);

  const popupOptions = useMemo(
    () => [
      {
        leadingIcon: { icon: { Component: Gallery, size: 'xs' } },
        onClick: () => {
          dispatch(updateSelectedPOI(activePOI));
          dispatch(updateLocationResult(activePOI));
          dispatch(updateShowPOIPopupMenu(false));
          dispatch(updateActivePOI(undefined));
          if (activePOI?.ID) {
            logCustomRoutePOIExploreSelected({ poi_id: activePOI?.ID });
          }
        },
        title: <FormattedMessage defaultMessage="Explore this spot" />
      },
      ...(canAddToRoute
        ? [
            {
              leadingIcon: { icon: { Component: Add, size: 'xs' } },
              onClick: () => {
                if (activePOI?._geoloc) {
                  routeToPoint({ coordinates: { lat: activePOI._geoloc.lat, lng: activePOI._geoloc.lng }, isPOIAdd: true });
                  dispatch(updateActivePOI(undefined));
                  dispatch(updateSelectedPOI(undefined));
                  dispatch(updateShowPOIPopupMenu(false));
                  dispatch(updateLocationResult(undefined));
                }
              },
              title: <FormattedMessage defaultMessage="Add to route" />
            }
          ]
        : [])
    ],
    [activePOI, canAddToRoute, dispatch, routeToPoint]
  );

  const exploreUrl = useMemo(
    () => wrapUrlSafe(`/explore/${activePOI?.slug}?${searchParams.toString()}`, languageRegionCode),
    [activePOI, languageRegionCode, searchParams]
  );

  const expPopupOptions = useMemo(
    () => [
      {
        leadingIcon: { icon: { Component: Gallery, size: 'xs' } },
        onClick: () => {
          if (canAddToRoute) {
            dispatch(updateSelectedPOI(activePOI));
            dispatch(updateLocationResult(activePOI));
            dispatch(updateShowPOIPopupMenu(false));
            dispatch(updateActivePOI(undefined));
            if (activePOI?.ID) {
              logCustomRoutePOIExploreSelected({ poi_id: activePOI?.ID });
            }
          } else {
            pushState(exploreUrl);
          }
        },
        title: <FormattedMessage defaultMessage="Explore this spot" />
      },
      {
        leadingIcon: { icon: { Component: Edit, size: 'xs' } },
        onClick: () => {
          if (activePOI?._geoloc) {
            if (canAddToRoute) {
              routeToPoint({ coordinates: { lat: activePOI._geoloc.lat, lng: activePOI._geoloc.lng }, isPOIAdd: true });
              dispatch(updateActivePOI(undefined));
              dispatch(updateSelectedPOI(undefined));
              dispatch(updateShowPOIPopupMenu(false));
              dispatch(updateLocationResult(undefined));
            } else {
              dispatch(setPendingFlyTo({ center: [activePOI._geoloc.lng, activePOI._geoloc.lat], zoom: 17 }));
              pushState(buildCustomRouteUrl());
            }
          }
        },
        title: canAddToRoute ? <FormattedMessage defaultMessage="Add to route" /> : <FormattedMessage defaultMessage="Build custom route" />
      }
    ],
    [activePOI, canAddToRoute, dispatch, exploreUrl, pushState, buildCustomRouteUrl, routeToPoint]
  );

  return (
    <>
      {activePOI && showPOIPopupMenu && (
        <ActivePOIPopupOptions
          options={isTapAnywhereEnabled ? expPopupOptions : popupOptions}
          poi={activePOI}
          isTapAnywhereEnabled={isTapAnywhereEnabled}
        />
      )}
    </>
  );
};

export default ActivePOIRoutingOptions;
